import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Rocket,
  User,
  FileText,
  TrendingUp,
  MessageCircle,
  Briefcase,
  Lightbulb,
  Check,
  Bookmark
} from 'lucide-react';
import { UserGuideCategory, UserGuideArticle } from '../types/userGuide.types';

interface UserGuideSidebarProps {
  categories: UserGuideCategory[];
  selectedArticle: UserGuideArticle | null;
  onArticleSelect: (article: UserGuideArticle) => void;
  completedArticles?: Set<string>;
  bookmarkedArticles?: Set<string>;
  className?: string;
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Rocket,
  User,
  FileText,
  TrendingUp,
  MessageCircle,
  Briefcase,
  Lightbulb,
};

const difficultyColors = {
  beginner: 'bg-green-100 text-green-800',
  intermediate: 'bg-yellow-100 text-yellow-800',
  advanced: 'bg-red-100 text-red-800',
};

const UserGuideSidebar: React.FC<UserGuideSidebarProps> = ({
  categories,
  selectedArticle,
  onArticleSelect,
  completedArticles = new Set(),
  bookmarkedArticles = new Set(),
  className = '',
}) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(categories.map(cat => cat.id))
  );

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  return (
    <div className={`bg-white border-r border-gray-200 overflow-y-auto ${className}`}>
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 rounded-lg bg-blue-600 flex items-center justify-center">
            <FileText className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">User Guide</h2>
            <p className="text-xs text-gray-600">Learn how to master FutureLens Pro</p>
          </div>
        </div>
      </div>

      <div className="py-4">
        <div className="px-4 mb-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Categories
          </h3>
        </div>

        {categories.map((category) => {
          const Icon = iconMap[category.icon] || FileText;
          const isExpanded = expandedCategories.has(category.id);
          const categoryCompleted = category.articles.filter(a =>
            completedArticles.has(a.id)
          ).length;
          const categoryTotal = category.articles.length;

          return (
            <div key={category.id} className="mb-2">
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left group"
              >
                <div className="flex items-center gap-3 flex-1">
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                    isExpanded ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                  }`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate text-gray-900">{category.name}</div>
                    {categoryTotal > 0 && (
                      <div className="text-xs text-gray-500">
                        {categoryCompleted}/{categoryTotal} completed
                      </div>
                    )}
                  </div>
                </div>
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {isExpanded && (
                <div className="mt-1 mb-2">
                  {category.articles.map((article) => {
                    const isSelected = selectedArticle?.id === article.id;
                    const isCompleted = completedArticles.has(article.id);
                    const isBookmarked = bookmarkedArticles.has(article.id);

                    return (
                      <button
                        key={article.id}
                        onClick={() => onArticleSelect(article)}
                        className={`w-full px-4 py-2 pl-14 flex items-center gap-2 text-left transition-colors ${
                          isSelected
                            ? 'bg-blue-50 border-l-2 border-blue-500'
                            : 'hover:bg-gray-50 border-l-2 border-transparent'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-sm ${isSelected ? 'text-blue-900 font-medium' : 'text-gray-700'}`}>
                              {article.title}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${difficultyColors[article.difficulty]}`}>
                              {article.difficulty}
                            </span>
                            <span className="text-xs text-gray-500">
                              {article.estimatedReadTime} min read
                            </span>
                            {isCompleted && (
                              <Check className="h-3 w-3 text-green-500" />
                            )}
                            {isBookmarked && (
                              <Bookmark className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UserGuideSidebar;
